// How the task list pages show their rows, persisted in localStorage so the
// choice sticks across items and sessions. useLogbookViewPrefs' shape, and its
// rules apply here word for word:
//
//   - The defaults are spread UNDER the stored object, so adding a key here
//     turns it on/off for everyone who never touched the control while an
//     explicit choice survives -- no storage-key bump when this list grows.
//   - setPref writes a FULL SNAPSHOT of {...defaults, ...stored}, so once a key
//     has been written the default no longer reaches that user. Which makes a
//     default flip a ONE-WAY DOOR: false -> true is free, true -> false is not,
//     because everyone who has touched any switch since would keep the old
//     value forever. That one needs a storage-key bump.
//
// ITS OWN KEY, not tp.logbook.view.v1, and the snapshot rule above is why: a
// task key written into the logbook's blob would ride there forever. Beyond
// that, `showNotes` has no meaning on a task row (notes are already stacked
// into the Name cell), and two separate key-spaces keep a future default flip
// on one page from being a one-way door on the other.

import { useState } from 'react';

const STORAGE_KEY = 'tp.tasks.view.v1';

export const TASK_VIEW_PREF_DEFAULTS = {
  // On: a task's photos ride a continuation row under it. Safe as a default of
  // TRUE because no release has ever written this key -- see the one-way door
  // note above.
  showPhotos: true,
};

function load() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const stored = raw ? JSON.parse(raw) : {};
    return { ...TASK_VIEW_PREF_DEFAULTS, ...(typeof stored === 'object' && stored ? stored : {}) };
  } catch {
    return { ...TASK_VIEW_PREF_DEFAULTS };
  }
}

export function useTaskViewPrefs() {
  const [prefs, setPrefs] = useState(load);

  function setPref(key, value) {
    setPrefs((current) => {
      const next = { ...current, [key]: Boolean(value) };
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Storage full/blocked: the toggle still works for this session.
      }
      return next;
    });
  }

  return [prefs, setPref];
}

export default useTaskViewPrefs;
